import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  errorHandler,
  downloadExcelFile,
  exportExcel,
  getAdmissionHistory,
  getExcelStorage,
  getLogData,
  getMeVerTwo,
  getStore,
  getStoreImagesInfo,
  getStores,
  getTodayReport,
  transformAccount,
  saveStoreDisplayInfo,
  saveThumbnail,
  saveThumbnailVerTwo,
  saveSignatureMenuImg,
  saveSignatureMenuImgVerTwo,
  deleteSignatureMenuImg,
  saveSignatureMenuImgOrder
} from 'apis';
import {
  ExcelStorageParams,
  ISaveAppDisplayInfo,
  IStoreImageOrderRequest,
  IStoreThumbnailRequest,
  StoreImagesType,
  StoreType
} from 'interfaces/store';
import { debounce } from 'utils/eventUtils';
import { setMyInfo } from 'utils/storages';
import { modalMethod } from 'components';
import { useMeInfo } from './useMeInfo';

const onSuccessStore = (data: StoreType) => {
  setMyInfo(data);
};

export const useStore = (sid: string, enabled: boolean) => {
  // const [key, setKey] = useState(sid);
  // useEffect(() => {
  //   console.log('change sid', sid);
  //   setKey(sid);
  // }, [sid]);
  const key = sid;
  return useQuery<StoreType>(['getStore', key], () => getStore(key), {
    enabled: !!key && enabled,
    onSuccess: onSuccessStore
  });
};

export const useStores = (config: Record<string, unknown>) => {
  return useQuery(['getStores'], () => getStores(), {
    ...config
  });
};

export const useTransformAccount = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ['transformAccount'],
    (data: { transactionId: string; id: string; isAdmin: boolean; token: string }) => transformAccount(data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['getMe']);
      },
      onError: async () => {
        await queryClient.invalidateQueries(['getMe']);
      }
    }
  );
};

export const useSaveAppDisplayInfo = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveAppDisplayInfo'], (params: ISaveAppDisplayInfo) => saveStoreDisplayInfo(params), {
    onSuccess: async () => {
      modalMethod.info('수정되었습니다.');
      await queryClient.invalidateQueries('getSettlementMetaInfo');
    }
  });
};

export const useSaveThumbnail = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveThumbnail'], (params: FormData) => saveThumbnail(params), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('getStoreVerTwo');
      await queryClient.invalidateQueries('getStoreImagesInfo');
    }
  });
};

export const useSaveThumbnailVerTwo = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveThumbnailVerTwo'], (params: IStoreThumbnailRequest) => saveThumbnailVerTwo(params), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('getStoreVerTwo');
      await queryClient.invalidateQueries('getStoreImagesInfo');
    }
  });
};

export const useSaveSignatureMenuImg = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveSignatureMenuImg'], (params: FormData) => saveSignatureMenuImg(params), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('getStoreVerTwo');
      await queryClient.invalidateQueries('getStoreImagesInfo');
    }
  });
};

export const useSaveSignatureMenuImgVerTwo = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveSignatureMenuImgVerTwo'], (params: IStoreImageOrderRequest) => saveSignatureMenuImgVerTwo(params), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('getStoreVerTwo');
      await queryClient.invalidateQueries('getStoreImagesInfo');
    }
  });
};
export const useSaveSignatureMenuImgOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveSignatureMenuImgOrder'], (params: FormData) => saveSignatureMenuImgOrder(params), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('getStoreVerTwo');
      await queryClient.invalidateQueries('getStoreImagesInfo');
    }
  });
};

export const useDeleteSignatureMenuImg = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ['deleteSignatureMenuImg'],
    (idxArray: { storeImageIdxList: number[] }) => deleteSignatureMenuImg(idxArray),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('getStoreVerTwo');
        await queryClient.invalidateQueries('getStoreImagesInfo');
      }
    }
  );
};

export const useTodayReport = (params: unknown, config: Record<string, unknown>) => {
  return useQuery(['getTodayReport', params], () => getTodayReport(params), {
    ...config
  });
};

export const useExportExcel = () => useMutation(['exportExcel'], (params: unknown) => exportExcel(params));

// 엑셀파일 보관함 목록 (said 기준, 50개 페이징 + 연도/제휴점/요청자료 필터)
export const useExcelStorage = (params: ExcelStorageParams) => {
  const { meInfo } = useMeInfo();
  return useQuery(['getExcelStorage', params], () => getExcelStorage(params), {
    // said(토큰) 기준 API — 로그인 계정 로드 완료 후 호출
    enabled: !!meInfo?.admin?.said,
    keepPreviousData: true
  });
};

export const useDownloadExcelFile = () =>
  useMutation(['downloadExcelFile'], (params: { fileId: string; sid: string; fileName: string }) =>
    downloadExcelFile(params.fileId, params.sid, params.fileName)
  );

export const useAdmissionHistory = (said: string) => {
  return useQuery(['getAdmissionHistory', said], () => getAdmissionHistory(said), {
    enabled: !!said,
    onError: errorHandler
  });
};

export const useLogData = (pageNum) => {
  const queryClient = useQueryClient();
  const invalidateQueries = () => {
    return debounce(queryClient.invalidateQueries('useLogData'), pageNum);
  };

  const query = useQuery(['useLogData', pageNum], () => getLogData(pageNum));

  return {
    ...query,
    invalidateQueries
  };
};

export const useMeVerTwo = (sid: string) => {
  const key = sid;
  return useQuery<StoreType>(['getStoreVerTwo', key], () => getMeVerTwo(key), {
    enabled: !!key,
    onSuccess: onSuccessStore
  });
};

export const useStoreImagesInfo = (sid: string) => {
  const key = sid;
  return useQuery<StoreImagesType>(['getStoreImagesInfo', key], () => getStoreImagesInfo(key), {
    enabled: !!key
  });
};
